<template lang="pug">
  .v-select(v-click-outside="hideItems")
    .v-select__field(
      :class="{ 'v-select__field--disabled': disabled, 'v-select__field--invalid': invalid, 'v-select__field--has-value': hasValue }"
      @click="toggle")
      input(type="text" style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;" tabindex="-1" autocomplete="username")
      input(type="password" style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;" tabindex="-1" autocomplete="current-password")
      input.v-select__field-input(
        v-focus
        v-if="toggled"
        v-model="search"
        type="text"
        :placeholder="searchPlaceholder"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        readonly
        @focus="makeEditable"
        @click.stop="makeEditable")
      .v-select__title(
        v-else
        :title="selectTitle"
        :class="{ 'v-select__title--hidden': selectTitle === label, 'v-select__label--invalid': invalid  }")
        | {{ selectTitle | translate | shortText }}
      label.v-select__label(
        :class="{ 'v-select__label--active': selectTitle !== label, 'v-select__label--invalid': invalid }")
        | {{ label | translate }}
      .v-select__bar
      .v-select__icon
        v-chevron(:value="toggled")
    .v-select__hint(v-if="hint") {{ hint }}
    transition(name="fade")
      .v-select__items(
        v-if="toggled"
        :class="{ 'v-select__items--fix-height': filteredOptions.length > 4 }")
        vue-scroll
          .item(
            v-for="item in filteredOptions"
            :key="item.id"
            @click="selectItem(item)")
            | {{ item[itemName] | translate }}
          .empty(v-if="filteredOptions.length === 0")
            | {{ 'base.no_options' | translate }}
</template>

<script>
import VChevron from '../v-chevron/VChevron'

export default {
  name: 'VSelectR',

  components: {
    VChevron
  },

  props: {
    label: String,
    value: [String, Number, Boolean, Object],
    options: {
      type: Array,
      default: () => ([])
    },
    searchPlaceholder: {
      type: String,
      default: null
    },
    itemValue: {
      type: String,
      default: 'id'
    },
    itemName: {
      type: String,
      default: 'name'
    },
    hint: {
      type: String,
      default: null
    },
    invalid: {
      type: Boolean,
      default: false
    },
    returnObject: Boolean,
    disabled: Boolean,
    apiSearch: Boolean
  },

  data: () => ({
    search: '',
    toggled: false
  }),

  filters: {
    shortText (text) {
      return text && text.length > 28
        ? (text.substring(0, 28) + '...')
        : text
    }
  },

  computed: {
    hasValue () {
      return this.value !== null && this.value !== undefined && this.value !== ''
    },

    selectTitle () {
      if (this.value !== null) {
        if (this.returnObject) {
          return this.value[this.itemName]
        } else {
          const selected = this.options
            .find(item => item[this.itemValue] === this.value)
          if (selected) {
            return selected[this.itemName]
          }
        }
      }
      return this.label
    },

    filteredOptions () {
      return this.apiSearch
        ? this.options
        : this.options
          .filter(item => item[this.itemName]
            .startsWith('constants')
            ? this.$t(item[this.itemName])
              .toLowerCase()
              .includes(this.search
                .toLowerCase())
            : item[this.itemName]
              .toLowerCase()
              .includes(this.search
                .toLowerCase()))
    }
  },

  methods: {
    selectItem (item) {
      this.toggled = !this.toggled
      this.$emit('input', this.returnObject ? item : item[this.itemValue])
    },

    toggle () {
      if (!this.disabled) {
        this.toggled = !this.toggled
      }
    },

    hideItems () {
      if (this.toggled) {
        this.toggled = false
      }
    },

    makeEditable (event) {
      event.target.removeAttribute('readonly')
    }
  },

  watch: {
    search (value) {
      this.$emit('search', value)
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/sass/abstracts/_variables.scss';

  .v-select {
    position: relative;

    &__field {
      position: relative;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      font-size: 1.5rem;
      width: 100%;
      height: 4rem;
      padding: 1rem 1.5rem;
      background: #eee;
      border-radius: 1rem;
      align-items: center;
      
      &--disabled {
        opacity: .5;
      }
      
      &--invalid {
        color: $color-red !important;
        border-bottom: .3rem solid $color-red !important;
      }

      &--has-value {
        background: #e0f5e9;
      }

      &-input {
        width: 100%;
        border: none;
        font-size: 1.4rem;

        &:focus {
          outline: none;
        }

        &:focus ~ label,
        &:valid ~ label {
          color: $color-gray;
          top: -1.7rem;
          left: 0;
          font-size: 1.3rem;
        }

        &:disabled ~ label {
          color: $color-gray-light;
        }

        &:focus ~ .v-select__bar:before,
        &:focus ~ .v-select__bar:after {
          width: 50%;
        }
      }
    }

    &__title {
      margin-right: 1.8rem;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      max-width: 24rem;
      font-size: 1.5rem;
      user-select: none;

      &--hidden {
        opacity: 0;
      }
    }

    &__label {
      color: $color-black;
      font-size: 1.5rem;
      position: absolute;
      pointer-events: none;
      top: 1.7rem;
      transition: 0.2s ease all;

      &--active {
        color: $color-gray;
        font-size: 1.3rem;
        top: -1.7rem;
        left: 0;
      }
      &--invalid {
        color: $color-red !important;
        font-size: 1.3rem;
        top: -1.7rem;
        left: 0;
      }
    }

    &__bar {
      &:before,
      &:after {
        content: "";
        height: .2rem;
        width: 0;
        bottom: 0;
        position: absolute;
        background: $color-primary;
        transition: 0.2s ease all;
      }

      &:before {
        left: 50%;
      }

      &:after {
        right: 50%;
      }
    }

    &__icon {
      transition: transform .3s ease;

      &--toggled {
        transform: rotate(-180deg);
      }
    }

    &__items {
      position: absolute;
      z-index: 1000;
      width: 100%;
      min-height: 4rem;
      background: $color-white;
      padding: 1rem 0;
      border-radius: 1rem;
      box-shadow: 2px 2px 5px 0px rgba(0, 0, 0, .2);

      &--fix-height {
        height: 21rem;
      }

      .item {
        cursor: pointer;
        user-select: none;
        position: relative;
        padding: 1rem;
        width: 100%;
        font-size: 1.5rem;

        &:hover {
          background: rgba(34, 166, 29, 0.4);
        }
      }

      .empty {
        font-size: 1.5rem;
        padding: .5rem 1rem;
      }
    }
    &__hint {
      color: $color-red;
      font-size: 1.2rem;
      font-weight: 300;
      position: relative;
    bottom: -2px;
    }
  }
</style>